import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/comics';

export default function About() {
  const [activeTab, setActiveTab] = useState<'about' | 'testimonials' | 'faq' | 'contact'>('about');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');

    try {
      const formEl = e.currentTarget;
      const formData = new FormData(formEl);
      const honeypotValue = (formData.get('website_alt') as string || '').trim();

      if (honeypotValue) {
        setFormStatus('success');
        setFormSubmitting(false);
        return;
      }

      formData.delete('website_alt');

      const formUrl = 'https://readdy.ai/api/form/d9oskp2qs38sj6jc7eq0';
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        formEl.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch (err) {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setFormSubmitting(false);
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'name': '关于我们 - 韩漫网站',
    'description': '了解韩漫网站的品牌故事，韩国漫画免费在线阅读平台，提供下拉式韩漫全集免费阅读服务。',
    'url': 'https://www.hanmanwangzhan.com/about',
    'inLanguage': 'zh-CN',
    'about': {
      '@type': 'Organization',
      'name': '韩漫网站',
      'description': siteInfo.description,
      'foundingDate': '2020',
      'contactPoint': {
        '@type': 'ContactPoint',
        'email': siteInfo.email,
        'contactType': 'customer service',
      },
    },
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': siteInfo.faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': '首页',
        'item': 'https://www.hanmanwangzhan.com/',
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': '关于我们',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <h1 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-900">
              关于韩漫网站
            </h1>
            <p className="text-foreground-500 mt-2 text-sm">
              韩国漫画免费在线阅读平台 —— 下拉式韩漫，好看的漫画大全
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-6">
              {[
                { key: 'about' as const, label: '品牌介绍' },
                { key: 'testimonials' as const, label: '用户口碑' },
                { key: 'faq' as const, label: '常见问题' },
                { key: 'contact' as const, label: '联系我们' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-4 text-sm font-medium border-b-2 transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        {activeTab === 'about' && (
          <section className="py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">品牌故事</h2>
                  <p className="text-foreground-500 mt-3 text-sm max-w-2xl mx-auto">
                    {siteInfo.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
                  <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                    <h3 className="text-lg font-semibold text-foreground-900 mb-4">我们的使命</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      韩漫网站成立于{siteInfo.founded}，致力于为广大中文读者提供最优质的<strong>韩国漫画免费</strong>在线阅读服务。我们相信每个人都应该有机会享受精彩的漫画内容，因此平台坚持免费阅读模式，让<strong>下拉式韩漫</strong>触手可及。
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                    <h3 className="text-lg font-semibold text-foreground-900 mb-4">平台规模</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      目前平台已收录<strong>{siteInfo.totalComics}</strong>部韩国漫画作品，涵盖多种类型。每日活跃读者超过<strong>{siteInfo.dailyVisitors}</strong>，是国内领先的<strong>漫画大全</strong>在线阅读平台之一。我们持续扩充内容库，确保读者总能找到好看的韩国漫画。
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">平台优势</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {siteInfo.features.map((feature, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-6 border border-background-200/70 hover:border-primary-200 transition-colors duration-200">
                      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                        <i className={`${feature.icon} text-xl`}></i>
                      </span>
                      <h3 className="font-semibold text-foreground-900 mb-2 text-sm">{feature.title}</h3>
                      <p className="text-foreground-500 text-xs leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Testimonials */}
        {activeTab === 'testimonials' && (
          <section className="py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">用户口碑</h2>
                  <p className="text-foreground-500 mt-3 text-sm">
                    听听读者们怎么说
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {siteInfo.testimonials.map((testimonial, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <div className="font-semibold text-foreground-900 text-sm">{testimonial.name}</div>
                          <div className="flex gap-0.5 mt-0.5">
                            {Array.from({ length: testimonial.rating }).map((_, j) => (
                              <span key={j} className="w-4 h-4 flex items-center justify-center text-accent-500">
                                <i className="ri-star-fill text-xs"></i>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-foreground-600 text-sm leading-relaxed">{testimonial.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {activeTab === 'faq' && (
          <section className="py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">常见问题</h2>
                  <p className="text-foreground-500 mt-3 text-sm">
                    关于韩国漫画在线阅读的常见问题解答
                  </p>
                </div>
                <div className="space-y-4">
                  {siteInfo.faqs.map((faq, i) => (
                    <details key={i} className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer text-sm font-medium text-foreground-900">
                        {faq.question}
                        <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                          <i className="ri-arrow-down-s-line"></i>
                        </span>
                      </summary>
                      <div className="px-6 pb-4 text-foreground-600 text-sm leading-relaxed">
                        {faq.answer}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Contact Form */}
        {activeTab === 'contact' && (
          <section className="py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">联系我们</h2>
                  <p className="text-foreground-500 mt-3 text-sm">
                    有任何问题或建议，欢迎随时联系我们
                  </p>
                </div>

                {formStatus === 'success' ? (
                  <div className="bg-background-100 rounded-lg p-8 border border-background-200/70 text-center">
                    <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </span>
                    <h3 className="text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                    <p className="text-foreground-500 text-sm">
                      感谢您的留言，我们会尽快回复您。
                    </p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="mt-6 px-6 py-2 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                    >
                      继续留言
                    </button>
                  </div>
                ) : (
                  <form
                    id="contact-form"
                    data-readdy-form
                    className="bg-background-100 rounded-lg p-8 border border-background-200/70 space-y-5"
                    onSubmit={handleContactSubmit}
                  >
                    {formStatus === 'error' && (
                      <div className="bg-primary-50 border border-primary-200 text-primary-700 rounded-lg px-4 py-3 text-sm">
                        {formError}
                      </div>
                    )}

                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200"
                        placeholder="请输入消息主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <button
                      type="submit"
                      disabled={formSubmitting}
                      className="w-full py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {formSubmitting ? '提交中...' : '发送留言'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Contact Info */}
        <section className="py-10 md:py-14 bg-background-100 border-t border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div>
                  <span className="w-10 h-10 mx-auto flex items-center justify-center text-primary-500">
                    <i className="ri-mail-line text-xl"></i>
                  </span>
                  <h4 className="text-sm font-semibold text-foreground-900 mt-3">电子邮箱</h4>
                  <p className="text-foreground-500 text-xs mt-1">{siteInfo.email}</p>
                </div>
                <div>
                  <span className="w-10 h-10 mx-auto flex items-center justify-center text-primary-500">
                    <i className="ri-time-line text-xl"></i>
                  </span>
                  <h4 className="text-sm font-semibold text-foreground-900 mt-3">服务时间</h4>
                  <p className="text-foreground-500 text-xs mt-1">周一至周日 9:00 - 22:00</p>
                </div>
                <div>
                  <span className="w-10 h-10 mx-auto flex items-center justify-center text-primary-500">
                    <i className="ri-question-answer-line text-xl"></i>
                  </span>
                  <h4 className="text-sm font-semibold text-foreground-900 mt-3">反馈时效</h4>
                  <p className="text-foreground-500 text-xs mt-1">24小时内回复</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}